import { useTheme } from '@emotion/react';
import NextLink from 'next/link';
import PropTypes from 'prop-types';

import Flex from 'shopper/components/Flex';
import Heading from 'shopper/components/Heading';
import Link from 'shopper/components/Link';
import Text from 'shopper/components/Text';

import ListItem from 'components/List/ListItem';

import { sendEvent } from 'lib/gtag';
import {
  toCategoryLink,
  toLandingPageLink,
  toSubcategoryLink,
} from 'lib/links';

import { useTopbar } from 'providers/TopbarProvider';

import BLACK_FRIDAY from 'constants/blackFriday';
import LINKS from 'constants/links';
import TOPBAR from 'constants/topbar';

const categoriesGroupedBySubcategories = (categoriesData, categoryGroupName) =>
  categoriesData
    .filter(({ groupName }) => groupName === categoryGroupName)
    .map(({ subcategories, categoryName, categoryUrl }) => ({
      categoryName,
      categoryUrl,
      subcategories,
    }))
    .sort((a, b) => b.subcategories.length - a.subcategories.length);

const ActiveCategoryGroupList = ({
  activeCategory,
  categories,
  lastCategorySlugGroupedBySubcategories,
  onCategoryClick,
  onClose,
}) => {
  const theme = useTheme();
  const { highlightedLandings } = useTopbar();

  return (
    <Flex
      flexDirection="column"
      position="relative"
      top={0}
      py={3}
      px={6}
      full
      onClick={() =>
        sendEvent({
          action: 'categories_select',
          category: TOPBAR.GA_EVENT_CATEGORY,
        })
      }
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor={theme.app.dropdown.border}
        pb={2}
      >
        <Heading as="span" size="size4">
          {activeCategory}
        </Heading>
        <NextLink href={LINKS.SITEMAP} passHref>
          <Link data-test-selector="goto-all-categories-list" onClick={onClose}>
            Ver todas as categorias
          </Link>
        </NextLink>
      </Flex>
      <Flex
        alignItems="flex-start"
        flexDirection="column"
        flexWrap="wrap"
        height="30rem"
        py={4}
      >
        {activeCategory === BLACK_FRIDAY.CATEGORY_NAME &&
          highlightedLandings.length > 0 &&
          highlightedLandings
            .sort((a) =>
              a.landingName === BLACK_FRIDAY.CATEGORY_NAME ? -1 : 1
            )
            .map(({ landingId, landingName, landingSlug, landingType }) => (
              <Flex
                key={landingId}
                alignItems="flex-start"
                flexDirection="column"
                height="fit-content"
                mb={2}
              >
                <NextLink
                  href={toLandingPageLink(landingSlug, landingType)}
                  key={landingId}
                  passHref
                >
                  <Link
                    type={
                      landingName === BLACK_FRIDAY.CATEGORY_NAME && 'secondary'
                    }
                    whiteSpace="break-spaces"
                    onClick={onCategoryClick}
                  >
                    {landingName}
                  </Link>
                </NextLink>
              </Flex>
            ))}
        {categoriesGroupedBySubcategories(categories, activeCategory).map(
          ({ categoryName, categoryUrl, subcategories }) => (
            <Flex
              alignItems="flex-start"
              flexDirection="column"
              height="fit-content"
              key={categoryName}
              mb={4}
            >
              <NextLink href={toCategoryLink(categoryUrl)} passHref>
                <Link
                  data-test-selector="goto-category-page"
                  type="secondary"
                  whiteSpace="break-spaces"
                  mb={1}
                  onClick={onCategoryClick}
                  bold
                >
                  {categoryName}
                </Link>
              </NextLink>
              {categoryUrl === lastCategorySlugGroupedBySubcategories &&
                highlightedLandings.length > 0 &&
                highlightedLandings[0].landingType !==
                  BLACK_FRIDAY.LANDING_TYPE && (
                  <>
                    <Text mt={4} mb={1} bold>
                      Páginas Especiais
                    </Text>
                    <ListItem
                      type="ghost"
                      display="flex"
                      flexDirection="column"
                    >
                      {highlightedLandings.map(
                        ({
                          landingId,
                          landingName,
                          landingSlug,
                          landingType,
                        }) =>
                          landingName && (
                            <NextLink
                              href={toLandingPageLink(landingSlug, landingType)}
                              key={landingId}
                              passHref
                            >
                              <Link
                                type="secondary"
                                size="size1"
                                mb={1}
                                onClick={onCategoryClick}
                              >
                                {landingName}
                              </Link>
                            </NextLink>
                          )
                      )}
                    </ListItem>
                  </>
                )}
              {subcategories.length > 0 && (
                <ListItem type="ghost" display="flex" flexDirection="column">
                  {subcategories.map(
                    ({ subcategoryName, subcategoryParam }) => (
                      <NextLink
                        key={subcategoryParam}
                        href={toSubcategoryLink(subcategoryParam)}
                        passHref
                      >
                        <Link
                          type="secondary"
                          size="size1"
                          data-test-selector="goto-subcategory-page"
                          mb={1}
                          onClick={onCategoryClick}
                        >
                          {subcategoryName}
                        </Link>
                      </NextLink>
                    )
                  )}
                </ListItem>
              )}
            </Flex>
          )
        )}
      </Flex>
    </Flex>
  );
};

ActiveCategoryGroupList.defaultProps = {
  activeCategory: null,
};

ActiveCategoryGroupList.propTypes = {
  activeCategory: PropTypes.string,
  categories: PropTypes.shape({
    categoryId: PropTypes.number,
    categoryName: PropTypes.string.isRequired,
    categoryUrl: PropTypes.string.isRequired,
    groupId: PropTypes.number.isRequired,
    groupName: PropTypes.string.isRequired,
    subcategories: PropTypes.array.isRequired,
  }).isRequired,
  lastCategorySlugGroupedBySubcategories: PropTypes.string.isRequired,
  onCategoryClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ActiveCategoryGroupList;
